import { useState, useEffect } from 'react';
import { useProfileSettings } from './ProfileSettingsContext';
import { processProfileDocument, parameterizeTypstTemplate, verifyTemplateCompilation } from '../../services/profileProcessingFunctions';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';

export default function ArchitectSection() {
  const { 
    selectedProfileId, setMessage,
    editingDetails, setEditingDetails,
    user, editingResumeGenConfig, setEditingResumeGenConfig
  } = useProfileSettings();

  const [extractingResume, setExtractingResume] = useState(false);
  const [extractingCv, setExtractingCv] = useState(false);
  const [parameterizing, setParameterizing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{ rendered: string; dataUsed: any } | null>(null);
  
  const [editingTypstSource, setEditingTypstSource] = useState('');

  useEffect(() => {
    if (editingResumeGenConfig?.customTypstSource) {
      setEditingTypstSource(editingResumeGenConfig.customTypstSource);
    } else {
      setEditingTypstSource('');
    }
  }, [editingResumeGenConfig?.customTypstSource]);

  const handleExtractAndIndex = async (type: 'resume' | 'cv') => {
    if (!selectedProfileId) return;
    const path = type === 'resume' ? editingDetails.defaultResumePath : editingDetails.defaultCvPath;
    if (!path) {
      setMessage({ type: 'error', text: `Please upload a ${type} first in the Source Documents section.` });
      return;
    }

    if (type === 'resume') setExtractingResume(true);
    else setExtractingCv(true);
    
    setMessage(null);

    try {
      const result = await processProfileDocument({
        profileId: selectedProfileId,
        storagePath: path,
        docType: type
      });

      if (result.success && result.details) {
        setEditingDetails((prev: any) => {
          const contact = result.details?.contact || {};
          
          return {
            ...prev,
            ...result.details, // This includes firstName, lastName, summary, etc.
            ...(contact && typeof contact === 'object' ? contact : {}),
            // Map specific URLs back to the state names
            linkedinUrl: contact.linkedin || prev.linkedinUrl,
            githubUrl: contact.github || prev.githubUrl,
            portfolioUrl: contact.website || prev.portfolioUrl,
            currentJobTitle: contact.role || prev.currentJobTitle,
          };
        });

        setMessage({ 
          type: 'success', 
          text: `Profile synced! Details extracted and semantic index updated.` 
        });
      }
    } catch (error: any) {
      console.error('Error extracting details:', error);
      setMessage({ type: 'error', text: `Extraction failed: ${error.message}` });
    } finally {
      setExtractingResume(false);
      setExtractingCv(false);
    }
  };

  const handleMagicParameterize = async () => {
    if (!editingTypstSource.trim()) return;
    setParameterizing(true);
    setMessage(null);
    try {
      const result = await parameterizeTypstTemplate({
        source: editingTypstSource
      });
      setEditingTypstSource(result.templatedSource);
      setMessage({ type: 'success', text: 'Magic Parameterization complete!' });
    } catch (error: any) {
      console.error('Error parameterizing:', error);
      setMessage({ type: 'error', text: `Parameterization failed: ${error.message}` });
    } finally {
      setParameterizing(false);
    }
  };

  const handleVerifyTemplate = async () => {
    if (!editingTypstSource.trim()) return;
    setVerifying(true);
    setMessage(null);
    try {
      const result = await verifyTemplateCompilation({
        template: editingTypstSource
      });
      if (result.success) {
        setVerificationResult({ rendered: result.rendered, dataUsed: result.dataUsed });
        setMessage({ type: 'success', text: 'Template verified successfully!' });
      } else {
        setMessage({ type: 'error', text: `Template error: ${result.error}` });
      }
    } catch (error: any) {
      console.error('Error verifying:', error);
      setMessage({ type: 'error', text: `Verification failed: ${error.message}` });
    } finally {
      setVerifying(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!user || !selectedProfileId) return;
    setSavingTemplate(true);
    setMessage(null);
    try {
      const db = getFirestore();
      const profileRef = doc(db, 'users', user.uid, 'autofillProfiles', selectedProfileId);
      
      const newConfig = {
        ...(editingResumeGenConfig || {}),
        customTypstSource: editingTypstSource
      };

      await updateDoc(profileRef, {
        resumeGenConfig: newConfig,
        updatedAt: new Date()
      });

      setEditingResumeGenConfig(newConfig);

      setMessage({ type: 'success', text: 'Typst template saved successfully!' });
    } catch (error: any) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: `Save failed: ${error.message}` });
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <div className="mt-6 border-t border-gray-100 dark:border-gray-700 pt-6">
      <div className="flex items-center gap-2 mb-6">
        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg">
          <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Resume Architect</h3>
          <p className="text-xs text-gray-500">End-to-end template matching and data extraction pipeline</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Step 1: Analysis & Extraction */}
        <div className="flex flex-col h-full p-6 bg-indigo-50/30 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/30 rounded-2xl relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <svg className="w-24 h-24 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                 <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
              </svg>
           </div>
           
           <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-[10px] font-bold">1</span>
              <h4 className="text-base font-bold text-gray-900 dark:text-white">AI Profile Intelligence</h4>
           </div>
           <p className="text-sm text-gray-500 mb-8 leading-relaxed max-w-sm">
             Analyze your master documents to automatically sync your profile fields and update your semantic brain for extension matching.
           </p>
           
           <div className="space-y-4 mt-auto">
              <button
                onClick={() => handleExtractAndIndex('resume')}
                disabled={extractingResume || !editingDetails.defaultResumePath}
                className="w-full flex items-center justify-between px-5 py-4 bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-900/30 shadow-md transition-all disabled:opacity-50 group/btn"
              >
                <div className="flex flex-col items-start transition-transform group-hover/btn:translate-x-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-0.5">Primary Source</span>
                  <span className="text-sm font-bold">
                    {extractingResume ? 'Processing Resume...' : 'Analyze Master Resume'}
                  </span>
                </div>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>

              <button
                onClick={() => handleExtractAndIndex('cv')}
                disabled={extractingCv || !editingDetails.defaultCvPath}
                className="w-full flex items-center justify-between px-5 py-4 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-2xl hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50 group/btn"
              >
                <div className="flex flex-col items-start transition-transform group-hover/btn:translate-x-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-0.5">Secondary Source</span>
                  <span className="text-sm font-bold">
                    {extractingCv ? 'Processing CV...' : 'Analyze Full CV'}
                  </span>
                </div>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
           </div>
        </div>

        {/* Step 2: Visual Template */}
        <div className="flex flex-col h-full p-6 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-2xl">
           <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-[10px] font-bold">2</span>
              <h4 className="text-base font-bold text-gray-900 dark:text-white">Typst Template Strategy</h4>
           </div>
           <p className="text-sm text-gray-500 mb-4 leading-relaxed">
             Configure the code structure for your auto-generated resumes.
           </p>
           
           <div className="space-y-3">
              <textarea
                value={editingTypstSource}
                onChange={(e) => setEditingTypstSource(e.target.value)}
                placeholder="Paste Typst source code..."
                className="w-full h-32 p-3 text-[10px] font-mono border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all resize-none"
              />
              <div className="flex flex-col gap-2">
                 <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleMagicParameterize}
                      disabled={parameterizing || !editingTypstSource.trim()}
                      className="px-3 py-2 text-[10px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-all"
                    >
                      {parameterizing ? 'Processing...' : 'Magic Parameterize'}
                    </button>
                    <button
                      onClick={handleVerifyTemplate}
                      disabled={verifying || !editingTypstSource.trim()}
                      className="px-3 py-2 text-[10px] font-bold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-all"
                    >
                      {verifying ? 'Verifying...' : 'Verify & Dry-Run'}
                    </button>
                 </div>
                 <button
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate || !editingTypstSource.trim()}
                    className="w-full px-4 py-2.5 text-[11px] font-bold bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-lg border border-indigo-100 dark:border-indigo-900/30 hover:bg-indigo-100 transition-all disabled:opacity-50"
                 >
                    {savingTemplate ? 'Saving...' : 'Save Configured Template'}
                 </button>
              </div>
           </div>
        </div>
      </div>

      {verificationResult && (
        <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-xl animate-in fade-in slide-in-from-bottom-2">
           <div className="flex items-center justify-between mb-4 border-b border-gray-100 dark:border-gray-700 pb-3">
              <div className="flex items-center gap-2">
                 <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                 <h4 className="text-sm font-bold text-gray-900 dark:text-white">Active Template Context</h4>
              </div>
              <button 
                onClick={() => setVerificationResult(null)} 
                className="text-[10px] font-bold text-gray-400 hover:text-indigo-600 transition-colors"
              >
                CLOSE PREVIEW
              </button>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                 <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Rendered Typst Output</div>
                 <pre className="p-3 bg-gray-50 dark:bg-gray-900 text-[9px] font-mono overflow-auto max-h-48 rounded-lg border border-gray-100 dark:border-gray-700 scrollbar-thin">
                    {verificationResult.rendered}
                 </pre>
              </div>
              <div className="space-y-2">
                 <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Injected Data Mapping</div>
                 <pre className="p-3 bg-gray-50 dark:bg-gray-900 text-[9px] font-mono overflow-auto max-h-48 rounded-lg border border-gray-100 dark:border-gray-700 scrollbar-thin">
                    {JSON.stringify(verificationResult.dataUsed, null, 2)}
                 </pre>
              </div>
           </div>
        </div>
      )}
    </div>
  );
}
